// Puerto verbatim de packages/hce/src/hce/paciente/asegurarEncounterDeHoy.ts.
// "Iniciar nueva consulta" necesita un Encounter real para 001
// (Admisión-Alta-Egreso, EDITA un Encounter existente — no lo crea) y para
// que 002/003/005/006/007/008 puedan vincular su nota a la consulta del día.
//
// Reusa el Encounter ambulatorio ya abierto hoy si existe (idempotente, no
// duplica si se hace clic más de una vez el mismo día), o crea uno nuevo.
// Filtra por clase AMB a propósito — para no confundir/reusar por accidente
// un Encounter de telemedicina (clase VR).
import type { MedplumClient } from '@medplum/core';
import type { Encounter } from '@medplum/fhirtypes';
import i18n from '../i18n';
import { enlacesDeFusion, esRegistroFusionado, RegistroFusionadoError } from './fusionEnlaces';

const V3_ACT_CODE = 'http://terminology.hl7.org/CodeSystem/v3-ActCode';

function rangoDeHoy(): { inicio: string; fin: string } {
  const ahora = new Date();
  const inicio = new Date(ahora);
  inicio.setHours(0, 0, 0, 0);
  const fin = new Date(ahora);
  fin.setHours(23, 59, 59, 999);
  return { inicio: inicio.toISOString(), fin: fin.toISOString() };
}

export async function asegurarEncounterDeHoy(medplum: MedplumClient, patientId: string): Promise<string> {
  // Fusión de fichas duplicadas (C-004): NUNCA se abre ni se reutiliza un Encounter sobre un registro fusionado (`replaced-by`, inactivo). Esto vale para
  // todos los llamadores (Iniciar consulta, nueva nota, receta, Pacientes), no solo para el botón: la URL directa o una pestaña vieja también chocan aquí.
  // Si no se puede leer el Patient, el error se propaga: no se abre a ciegas.
  const paciente = await medplum.readResource('Patient', patientId);
  if (esRegistroFusionado(paciente)) {
    throw new RegistroFusionadoError(i18n.t('fichaPaciente.fusion.consultaBloqueada'), enlacesDeFusion(paciente).find((e) => e.tipo === 'replaced-by')?.id ?? null);
  }
  const patientRef = `Patient/${patientId}`;
  const { inicio, fin } = rangoDeHoy();
  // El search param `date` de Encounter (Encounter.period) sin prefijo NO
  // matchea contra un period abierto (sin `end`) en el servidor real —
  // verificado en vivo. `searchResources` no acepta el mismo query param
  // repetido en un objeto plano, así que se arma el query string a mano.
  const abiertos = await medplum.searchResources(
    'Encounter',
    `patient=${encodeURIComponent(patientRef)}&class=AMB&status=in-progress&date=ge${encodeURIComponent(inicio)}&date=le${encodeURIComponent(fin)}&_count=1`
  );
  const existente = (abiertos as Encounter[])[0];
  if (existente?.id) {
    return `Encounter/${existente.id}`;
  }

  const creado = await medplum.createResource<Encounter>({
    resourceType: 'Encounter',
    status: 'in-progress',
    class: { system: V3_ACT_CODE, code: 'AMB' },
    subject: { reference: patientRef },
    period: { start: new Date().toISOString() },
    // Alertas de completitud en vivo (chequeoCompletitudConsulta.ts) y el
    // widget de admin (packages/web/src/api/lib/calidad-hc-vivo.ts) necesitan
    // saber quién atendió — este es el ÚNICO lugar del sistema que crea un
    // Encounter (Formulario001 solo EDITA uno existente), así que es la
    // única fuente de verdad posible para "último profesional". Encounters
    // creados antes de este cambio no lo tienen — se resuelve solo con el
    // uso normal, no hace falta backfill (los widgets miran 30 días).
    ...(medplum.getProfile()?.id ? { participant: [{ individual: { reference: `Practitioner/${medplum.getProfile()!.id}` } }] } : {}),
  });
  return `Encounter/${creado.id}`;
}

/**
 * Tras un tiempo agotado de la Sala (el servidor puede seguir abriendo el Encounter), espera brevemente y vuelve a buscar el Encounter ambulatorio abierto
 * de hoy ANTES de que la ficha cree uno propio: así no se duplican Encounters AMB. Devuelve su referencia, o `null` si no apareció (entonces
 * `asegurarEncounterDeHoy` lo crea). `dormir` es inyectable para las pruebas.
 */
export async function esperarEncounterAmbulatorioAbierto(
  medplum: MedplumClient,
  patientId: string,
  o: { intentos?: number; esperaMs?: number; dormir?: (ms: number) => Promise<void> } = {}
): Promise<string | null> {
  const { intentos = 3, esperaMs = 2000, dormir = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)) } = o;
  const { inicio, fin } = rangoDeHoy();
  const query = `patient=${encodeURIComponent(`Patient/${patientId}`)}&class=AMB&status=in-progress&date=ge${encodeURIComponent(inicio)}&date=le${encodeURIComponent(fin)}&_count=1`;
  for (let i = 0; i < intentos; i++) {
    await dormir(esperaMs);
    try {
      const abiertos = (await medplum.searchResources('Encounter', query)) as Encounter[];
      if (abiertos[0]?.id) return `Encounter/${abiertos[0].id}`;
    } catch {
      // un fallo de la búsqueda no impide seguir: se reintenta y, al final, la ficha decide
    }
  }
  return null;
}

/**
 * Variante de SOLO LECTURA para las alertas de completitud en vivo — a
 * diferencia de `asegurarEncounterDeHoy`, NUNCA crea un Encounter (entrar a
 * mirar la ficha sin haber iniciado consulta no debe generar una consulta
 * fantasma). Sin filtro de `class` ni `status` (a diferencia de la función
 * de arriba, que solo mira AMB `in-progress`): interesa encontrar el
 * Encounter de hoy sea AMB o ya IMP, y sea que siga abierto o ya se haya
 * dado de alta (`finished`) — la regla de epicrisis justamente dispara
 * cuando un IMP de hoy YA está `finished`. `_sort=-date` para quedarse con
 * el más reciente si hubiera más de uno el mismo día.
 */
/**
 * Query (solo lectura) del Encounter ABIERTO del paciente, sin filtro de
 * fecha ni de clase — para Enfermería: una estancia de internación (IMP)
 * arrancó hace días, así que `buscarEncounterAbiertoDeHoy` (acotada a hoy) no
 * la encuentra, y una consulta ambulatoria abierta hoy sí entra igual.
 * `_sort=-date` para quedarse con la más reciente si hubiera más de una.
 * Pura (string), se prueba en asegurarEncounterDeHoy.test.ts.
 */
export function queryEncounterAbierto(patientId: string): string {
  return `patient=${encodeURIComponent(`Patient/${patientId}`)}&status=in-progress&_sort=-date&_count=1`;
}

/** Query (solo lectura) de la ESTANCIA de internación abierta — el Kardex 022 solo vale dentro de una (class AMB = receta ambulatoria, no entra). */
export function queryEstanciaAbierta(patientId: string): string {
  return `patient=${encodeURIComponent(`Patient/${patientId}`)}&class=IMP&status=in-progress&_sort=-date&_count=1`;
}

/**
 * El Encounter abierto del paciente, sea la estancia de internación o la
 * consulta ambulatoria en curso. NUNCA crea uno: Enfermería no abre consultas
 * (decisión 2026-09-13, reflejada también en el AccessPolicy "Enfermera",
 * donde `Encounter` es `readonly`). `null` = no hay nada abierto, y entonces
 * lo que registre la enfermera queda sin vínculo a un Encounter, igual que
 * antes de este cambio.
 */
export async function buscarEncounterAbiertoDePaciente(medplum: MedplumClient, patientId: string): Promise<Encounter | null> {
  const encontrados = await medplum.searchResources('Encounter', queryEncounterAbierto(patientId));
  return (encontrados as Encounter[])[0] ?? null;
}

/** La estancia de internación abierta (class IMP, in-progress), o `null`. Solo lectura, nunca crea. */
export async function buscarEstanciaAbierta(medplum: MedplumClient, patientId: string): Promise<Encounter | null> {
  const encontrados = await medplum.searchResources('Encounter', queryEstanciaAbierta(patientId));
  return (encontrados as Encounter[])[0] ?? null;
}

export async function buscarEncounterAbiertoDeHoy(medplum: MedplumClient, patientId: string): Promise<Encounter | null> {
  const patientRef = `Patient/${patientId}`;
  const { inicio, fin } = rangoDeHoy();
  const encontrados = await medplum.searchResources(
    'Encounter',
    `patient=${encodeURIComponent(patientRef)}&date=ge${encodeURIComponent(inicio)}&date=le${encodeURIComponent(fin)}&_sort=-date&_count=1`
  );
  return (encontrados as Encounter[])[0] ?? null;
}
